import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { baseURL } from '../../helpers/baseURL';
import { formatDateTime } from '../../helpers/formatDate';
import './public-ticket-track.css';

function formatWhen(iso) {
    if (!iso) return '';
    return formatDateTime(iso);
}

function formatStatus(name) {
    if (!name) return '';
    const normalized = String(name).replace(/_/g, ' ');
    return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function TicketProgress({ steps }) {
    if (!steps?.length) {
        return null;
    }

    return (
        <div className="public-ticket-track__progress" aria-label="Ticket progress">
            <div className="public-ticket-track__progress-track">
                {steps.map((step, index) => (
                    <React.Fragment key={step.key}>
                        {index > 0 && (
                            <span
                                className={`public-ticket-track__progress-line${
                                    steps[index - 1]?.state === 'done' ? ' is-done' : ''
                                }`}
                                aria-hidden
                            />
                        )}
                        <span
                            className={`public-ticket-track__progress-step is-${step.state}`}
                            title={step.label}
                        >
                            <span className="public-ticket-track__progress-dot" aria-hidden />
                            <span className="public-ticket-track__progress-label">{step.label}</span>
                        </span>
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

function CloseConfirmDialog({ open, onCancel, onConfirm, closing }) {
    if (!open) {
        return null;
    }

    return (
        <div
            className="public-ticket-track__dialog-backdrop"
            role="presentation"
            onClick={closing ? undefined : onCancel}
        >
            <div
                className="public-ticket-track__dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="close-ticket-title"
                onClick={(e) => e.stopPropagation()}
            >
                <h2 id="close-ticket-title" className="public-ticket-track__dialog-title">
                    Close this ticket?
                </h2>
                <p className="public-ticket-track__dialog-body">
                    Our team will be notified. Once closed, it cannot be reopened.
                </p>
                <div className="public-ticket-track__dialog-actions">
                    <button
                        type="button"
                        className="public-ticket-track__btn public-ticket-track__btn--secondary"
                        onClick={onCancel}
                        disabled={closing}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="public-ticket-track__btn public-ticket-track__btn--primary"
                        onClick={onConfirm}
                        disabled={closing}
                    >
                        {closing ? 'Closing…' : 'Close ticket'}
                    </button>
                </div>
            </div>
        </div>
    );
}

const PublicTicketTrack = () => {
    const { token } = useParams();
    const [ticket, setTicket] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [replyText, setReplyText] = useState('');
    const [replyError, setReplyError] = useState(null);
    const [sending, setSending] = useState(false);
    const [closing, setClosing] = useState(false);
    const [actionError, setActionError] = useState(null);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    const loadTicket = useCallback(() => {
        if (!token) {
            return Promise.reject();
        }
        return axios
            .get(`${baseURL}public/tickets/${encodeURIComponent(token)}`)
            .then((res) => {
                setTicket(res.data);
                return res.data;
            });
    }, [token]);

    useEffect(() => {
        if (!token) {
            setError('Invalid link.');
            setLoading(false);
            return;
        }
        loadTicket()
            .catch(() => setError('This ticket could not be found or the link has expired.'))
            .finally(() => setLoading(false));
    }, [token, loadTicket]);

    const handleReply = async (e) => {
        e.preventDefault();
        const message = replyText.trim();
        if (!message || !token || !ticket?.can_reply) {
            return;
        }
        setReplyError(null);
        setSending(true);
        try {
            const res = await axios.post(
                `${baseURL}public/tickets/${encodeURIComponent(token)}/replies`,
                { message }
            );
            setTicket(res.data);
            setReplyText('');
        } catch (err) {
            const msg =
                err.response?.data?.message ||
                err.response?.data?.errors?.message?.[0] ||
                'Could not send your message. Please try again.';
            setReplyError(msg);
        } finally {
            setSending(false);
        }
    };

    const handleClose = async () => {
        if (!token || !ticket?.can_close) {
            return;
        }
        setActionError(null);
        setClosing(true);
        try {
            const res = await axios.post(
                `${baseURL}public/tickets/${encodeURIComponent(token)}/close`
            );
            setTicket(res.data);
            setShowCloseConfirm(false);
        } catch (err) {
            setActionError(
                err.response?.data?.message || 'Could not close this ticket. Please try again.'
            );
        } finally {
            setClosing(false);
        }
    };

    const timeline = ticket?.timeline || [];
    const replyItems = timeline.filter((item) => item.type === 'reply');
    const statusItems = timeline.filter((item) => item.type === 'status');
    const referenceLabel = ticket?.reference ? `Ref. ${ticket.reference}` : 'Your ticket';

    return (
        <div className="public-ticket-track">
            <div className="public-ticket-track__shell">
                {loading && <p className="public-ticket-track__muted">Loading…</p>}
                {error && <p className="public-ticket-track__error">{error}</p>}
                {ticket && (
                    <>
                        <header className="public-ticket-track__header">
                            <p className="public-ticket-track__eyebrow">{referenceLabel}</p>
                            <h1 className="public-ticket-track__title">{ticket.title}</h1>
                            <p className="public-ticket-track__status-line">
                                <span className="public-ticket-track__status-name">
                                    {formatStatus(ticket.status)}
                                </span>
                                <span className="public-ticket-track__status-sep">·</span>
                                <span className="public-ticket-track__dates">
                                    Opened {formatWhen(ticket.created_at)}
                                </span>
                            </p>
                        </header>

                        <TicketProgress steps={ticket.progress} />

                        {ticket.is_spam && (
                            <p className="public-ticket-track__banner" role="status">
                                This ticket is no longer active. You can no longer send messages.
                            </p>
                        )}

                        {ticket.is_rejected && !ticket.is_spam && (
                            <p className="public-ticket-track__banner" role="status">
                                This ticket is rejected. You can no longer send messages or reopen it.
                            </p>
                        )}

                        {ticket.is_closed && !ticket.is_spam && !ticket.is_rejected && (
                            <p className="public-ticket-track__banner" role="status">
                                This ticket is closed. You can no longer send messages or reopen it.
                            </p>
                        )}

                        {ticket.initial_message && (
                            <section className="public-ticket-track__block">
                                <h2 className="public-ticket-track__heading">Your request</h2>
                                <p className="public-ticket-track__message">{ticket.initial_message}</p>
                            </section>
                        )}

                        <section className="public-ticket-track__block">
                            <h2 className="public-ticket-track__heading">Conversation</h2>
                            {replyItems.length === 0 && (
                                <p className="public-ticket-track__muted">
                                    No replies yet. Our support team will respond here when they have an update.
                                </p>
                            )}
                            {replyItems.length > 0 && (
                                <ul className="public-ticket-track__thread">
                                    {replyItems.map((item) => {
                                        const isSupport = item.role === 'agent';

                                        return (
                                            <li
                                                key={item.id}
                                                className={`public-ticket-track__message-row${
                                                    isSupport
                                                        ? ' public-ticket-track__message-row--incoming'
                                                        : ' public-ticket-track__message-row--outgoing'
                                                }`}
                                            >
                                                <div className="public-ticket-track__bubble">
                                                    <div className="public-ticket-track__message-meta">
                                                        <strong>{item.author}</strong>
                                                        <time dateTime={item.created_at}>
                                                            {formatWhen(item.created_at)}
                                                        </time>
                                                    </div>
                                                    <div className="public-ticket-track__message-body">
                                                        {item.body || ''}
                                                    </div>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            {statusItems.length > 0 && (
                                <ul className="public-ticket-track__events">
                                    {statusItems.map((item) => (
                                        <li key={item.id} className="public-ticket-track__event">
                                            <span>{item.status_label || 'Status updated'}</span>
                                            <time dateTime={item.created_at}>
                                                {formatWhen(item.created_at)}
                                            </time>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </section>

                        {ticket.can_reply && (
                            <section className="public-ticket-track__block public-ticket-track__block--compose">
                                <h2 className="public-ticket-track__heading">Reply</h2>
                                <p className="public-ticket-track__muted public-ticket-track__hint">
                                    Your message goes to our support team. They will be notified by email.
                                </p>
                                <form className="public-ticket-track__reply-form" onSubmit={handleReply}>
                                    <textarea
                                        className="public-ticket-track__textarea"
                                        rows={4}
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        placeholder="Write your message…"
                                        maxLength={10000}
                                        disabled={sending}
                                    />
                                    {replyError && (
                                        <p className="public-ticket-track__error">{replyError}</p>
                                    )}
                                    <div className="public-ticket-track__actions">
                                        <button
                                            type="submit"
                                            className="public-ticket-track__btn public-ticket-track__btn--primary"
                                            disabled={sending || !replyText.trim()}
                                        >
                                            {sending ? 'Sending…' : 'Send'}
                                        </button>
                                        {ticket.can_close && (
                                            <button
                                                type="button"
                                                className="public-ticket-track__btn public-ticket-track__btn--secondary"
                                                onClick={() => setShowCloseConfirm(true)}
                                                disabled={closing || sending}
                                            >
                                                Close ticket
                                            </button>
                                        )}
                                    </div>
                                    {actionError && (
                                        <p className="public-ticket-track__error">{actionError}</p>
                                    )}
                                </form>
                            </section>
                        )}

                        <CloseConfirmDialog
                            open={showCloseConfirm}
                            onCancel={() => setShowCloseConfirm(false)}
                            onConfirm={handleClose}
                            closing={closing}
                        />
                    </>
                )}
            </div>
        </div>
    );
};

export default PublicTicketTrack;
